'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import {LOCAL_STORAGE_VALUES, WEBSHOP_COLORS} from '../utils/constants';
import {useTranslations} from 'next-intl';
import {WorkspaceData} from '../types/auth';
import {getFromLS, setToLS} from '../utils/localStorage';
import {useRouter} from '../i18n/navigation';
import {ROUTES} from '../utils/routes';

type Workspace = {
  id: number | null;
  type: 'organisation' | 'discovery';
  name: string;
  logo?: string | null;
  colors?: {
    header: string | null;
    main: string | null;
    text: string | null;
    category: string | null;
    subscribeButton: string | null;
    subscribeButtonBorder: string | null;
  };
};

type WorkspaceContextType = {
  currentWorkspace: Workspace;
  availableWorkspaces: Workspace[];
  isLoading: boolean;
  initializeWorkspaces: (
    authOrg: Partial<WorkspaceData>,
    withRedirect?: boolean
  ) => void;
  switchWorkspace: (orgId: number) => void;
  resetToDefaultWorkspace: () => void;
};

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(
  undefined
);

export function WorkspaceProvider({children}: {children: React.ReactNode}) {
  const t = useTranslations();
  const router = useRouter();

  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(
    null
  );
  const [availableWorkspaces, setAvailableWorkspaces] = useState<Workspace[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);

  const DEFAULT_WORKSPACE: Workspace = {
    id: 0,
    type: 'discovery',
    name: `Purpose · ${t('PageNames.discovery')}`,
    colors: WEBSHOP_COLORS.default
  };

  useEffect(() => {
    setIsLoading(true);
    const workspaceInStorage = getFromLS(
      LOCAL_STORAGE_VALUES.workspaces.current
    );
    const workspacesInStorage = getFromLS(LOCAL_STORAGE_VALUES.workspaces.all);
    if (workspaceInStorage) {
      setCurrentWorkspace(workspaceInStorage);
    }
    setAvailableWorkspaces(workspacesInStorage);
    setIsLoading(false);
  }, []);

  const setWorkspace = useCallback(
    (workspace: Workspace, withRedirect = true) => {
      setCurrentWorkspace(workspace);
      setToLS(LOCAL_STORAGE_VALUES.workspaces.current, workspace);

      if (withRedirect) {
        if (workspace.id) {
          router.push(`${ROUTES.webshop}/${workspace.id}`);
        } else {
          router.push(ROUTES.feed);
        }
      }
    },
    []
  );

  const switchWorkspace = useCallback(
    (orgId: number, withRedirect = true) => {
      setIsLoading(true);

      const workspace = availableWorkspaces?.find(space => space.id == orgId);
      if (!workspace) return;

      setWorkspace(workspace, withRedirect);

      setIsLoading(false);
    },
    [availableWorkspaces]
  );

  const initializeWorkspaces = useCallback(
    (authOrg: Partial<WorkspaceData>, withRedirect = true) => {
      setIsLoading(true);

      const workspaces = [DEFAULT_WORKSPACE];

      if (authOrg?.id) {
        const workspace: Workspace = {
          id: authOrg?.id,
          type: 'organisation',
          name: authOrg?.name || '',
          logo: authOrg?.logo || null,
          colors: WEBSHOP_COLORS[authOrg?.webshop_color || 'default']
        };
        workspaces.push(workspace);

        setWorkspace(workspace, withRedirect);
      } else {
        setWorkspace(DEFAULT_WORKSPACE, withRedirect);
      }

      setAvailableWorkspaces(workspaces);
      setToLS(LOCAL_STORAGE_VALUES.workspaces.all, workspaces);

      setIsLoading(false);
    },
    []
  );

  const resetToDefaultWorkspace = useCallback(() => {
    setIsLoading(true);

    if (currentWorkspace?.id) {
      switchWorkspace(0, true);
    }

    setAvailableWorkspaces([DEFAULT_WORKSPACE]);
    setToLS(LOCAL_STORAGE_VALUES.workspaces.all, [DEFAULT_WORKSPACE]);

    setIsLoading(false);
  }, []);

  return (
    <WorkspaceContext.Provider
      value={{
        currentWorkspace: currentWorkspace as Workspace,
        availableWorkspaces,
        initializeWorkspaces,
        switchWorkspace,
        resetToDefaultWorkspace,
        isLoading
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
